import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, Clock, DollarSign,
  Phone, Mail, MapPin, Search, Loader2, Plus, CreditCard, Send, Copy, ExternalLink,
  Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { FormField } from "../components/ui/form-field";
import { DatePickerUS } from "../components/ui/date-picker-us";
import { DollarInput } from "../components/ui/dollar-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { SendMenu } from "../components/SendMenu";
import { AdminLayout } from "../components/AdminLayout";
import { useI18n } from "../i18n/I18nContext";
import { useConfirm } from "../hooks/useConfirm";

const STATUS_INFO = {
  em_atraso: { label: "Em atraso", Icon: AlertTriangle, cls: "bg-rose-50 text-rose-800 border-rose-200" },
  vence_em_breve: { label: "Vence em breve", Icon: Clock, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  em_dia: { label: "Em dia", Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

const FILTERS = [
  { v: "em_atraso", label: "Em atraso" },
  { v: "vence_em_breve", label: "Vence em breve" },
  { v: "em_dia", label: "Em dia" },
  { v: "", label: "Todos" },
];

function fmtUSD(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

export default function Receivables() {
  const { t } = useI18n();
  const nav = useNavigate();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("em_atraso");
  const [search, setSearch] = useState("");
  const [payingFor, setPayingFor] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/receivables");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function handleEdit(r) {
    nav(`/clients?edit=${r.client_id}`);
  }

  async function handleDelete(r) {
    const ok = await confirm({
      title: t("Excluir cobrança recorrente?"),
      description: t("Esta ação removerá a configuração de cobrança (dia de vencimento e valor) do cliente {name}. Ele deixará de aparecer nesta lista. As limpezas e pagamentos já registrados serão mantidos.").replace("{name}", r.client_name || ""),
      destructive: true,
      actionLabel: t("Excluir"),
    });
    if (!ok) return;
    setDeletingId(r.client_id);
    try {
      await api.patch(`/clients/${r.client_id}`, {
        payment_due_day: null,
        value: 0,
      });
      toast.success(t("Cobrança removida"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha ao excluir"));
    } finally {
      setDeletingId(null);
    }
  }

  const filtered = useMemo(() => {
    let r = rows;
    if (filter) r = r.filter((x) => x.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((x) =>
        (x.client_name || "").toLowerCase().includes(q) ||
        (x.address || "").toLowerCase().includes(q),
      );
    }
    return r;
  }, [rows, filter, search]);

  const totals = useMemo(() => ({
    em_atraso: rows.filter((r) => r.status === "em_atraso").length,
    vence_em_breve: rows.filter((r) => r.status === "vence_em_breve").length,
    em_dia: rows.filter((r) => r.status === "em_dia").length,
    valor_atraso: rows
      .filter((r) => r.status === "em_atraso")
      .reduce((acc, r) => acc + Number(r.value || 0), 0),
  }), [rows]);

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="receivables-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <DollarSign className="text-emerald-600" /> {t("Recebimentos")}
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          {t("Acompanhe pagamentos previstos por cliente. Quem está em atraso, quem vai vencer e quem está em dia.")}
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label={t("Em atraso")} value={totals.em_atraso} accent="rose" icon={AlertTriangle} />
        <StatCard label={t("Vence em breve")} value={totals.vence_em_breve} accent="amber" icon={Clock} />
        <StatCard label={t("Em dia")} value={totals.em_dia} accent="emerald" icon={CheckCircle2} />
        <StatCard label={t("Valor em atraso")} value={fmtUSD(totals.valor_atraso)} accent="slate" icon={DollarSign} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.v || "all"}
            data-testid={`receivables-filter-${f.v || "all"}`}
            onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              filter === f.v
                ? "bg-blue-600 border-blue-600 text-white"
                : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
            }`}
          >
            {t(f.label)}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="cm-input"
            style={{ paddingLeft: "32px", width: "260px" }}
            placeholder={t("Buscar cliente ou endereço…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> {t("Carregando…")}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="receivables-empty">
          {t("Nenhum cliente nesse filtro.")} <br />
          {t("Para registrar cobranças, defina o")} <strong>{t("dia de pagamento")}</strong> {t("e")} <strong>{t("valor")}</strong> {t("em cada cliente.")}
          {" "}<Link to="/clients" className="text-blue-600 hover:underline">{t("Ir para clientes →")}</Link>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((r) => {
            const st = STATUS_INFO[r.status] || STATUS_INFO.em_dia;
            const StIcon = st.Icon;
            return (
              <li
                key={r.client_id}
                data-testid={`receivable-${r.client_id}`}
                className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${st.cls}`}>
                    <StIcon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{r.client_name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap mt-0.5">
                      {r.address && (
                        <span className="flex items-center gap-1"><MapPin size={11} />{r.address}</span>
                      )}
                      {r.phone && (
                        <span className="flex items-center gap-1"><Phone size={11} />{r.phone}</span>
                      )}
                      {r.email && (
                        <span className="flex items-center gap-1"><Mail size={11} />{r.email}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("Vencimento")}</div>
                    <div className="text-sm font-semibold">{fmtDate(r.next_due_date)}</div>
                    {r.days_overdue > 0 && (
                      <div className="text-[11px] text-rose-700 font-semibold">
                        {r.days_overdue} {r.days_overdue > 1 ? t("dias atrás") : t("dia atrás")}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("Valor")}</div>
                    <div className="text-lg font-bold text-slate-900">{fmtUSD(r.value)}</div>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${st.cls} inline-flex items-center gap-1`}>
                    <StIcon size={11} /> {t(st.label)}
                  </span>
                  <div className="flex items-center gap-2">
                    <PaymentLinkButton receivable={r} />
                    <Button
                      data-testid={`receivable-pay-${r.client_id}`}
                      onClick={() => setPayingFor(r)}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Plus size={13} className="mr-1" /> {t("Registrar")}
                    </Button>
                    <SendMenu
                      kind={t("Lembrete de pagamento")}
                      record={r}
                      testIdPrefix={`receivable-send-${r.client_id}`}
                    />
                    <Button
                      data-testid={`receivable-edit-${r.client_id}`}
                      onClick={() => handleEdit(r)}
                      size="sm"
                      variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-50"
                      title={t("Editar")}
                    >
                      <Pencil size={13} className="mr-1" /> {t("Editar")}
                    </Button>
                    <Button
                      data-testid={`receivable-delete-${r.client_id}`}
                      onClick={() => handleDelete(r)}
                      size="sm"
                      variant="outline"
                      disabled={deletingId === r.client_id}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      title={t("Excluir")}
                    >
                      {deletingId === r.client_id ? (
                        <Loader2 size={13} className="animate-spin mr-1" />
                      ) : (
                        <Trash2 size={13} className="mr-1" />
                      )}
                      {t("Excluir")}
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <RegisterPaymentDialog
        receivable={payingFor}
        onClose={(reload) => {
          setPayingFor(null);
          if (reload) load();
        }}
      />
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, accent, icon: Icon }) {
  const colors = {
    rose: "text-rose-700",
    amber: "text-amber-700",
    emerald: "text-emerald-700",
    slate: "text-slate-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`flex items-center gap-2 text-sm ${colors[accent] || "text-slate-700"}`}>
        <Icon size={16} />
        <span className="font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-1.5">{value}</div>
    </div>
  );
}

function RegisterPaymentDialog({ receivable, onClose }) {
  const { t } = useI18n();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("zelle");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [paidForDate, setPaidForDate] = useState("");

  useEffect(() => {
    if (receivable) {
      setAmount(String(receivable.value || ""));
      setPaidForDate(receivable.next_due_date || "");
      setMethod("zelle");
      setNotes("");
    }
  }, [receivable]);

  async function save() {
    setSaving(true);
    try {
      await api.post("/client-payments", {
        client_id: receivable.client_id,
        amount: Number(amount) || 0,
        method,
        paid_for_date: paidForDate || null,
        notes,
      });
      toast.success(t("Pagamento registrado"));
      onClose(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  if (!receivable) return null;
  return (
    <Dialog open={!!receivable} onOpenChange={(v) => !v && !saving && onClose(false)}>
      <DialogContent className="max-w-md" data-testid="register-payment-dialog">
        <DialogHeader>
          <DialogTitle>{t("Registrar pagamento —")} {receivable.client_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label={t("Valor recebido")} required>
            <DollarInput value={amount} onChange={setAmount} testId="payment-amount" />
          </FormField>
          <FormField label={t("Referente ao vencimento de")}>
            <DatePickerUS
              testId="payment-paid-for"
              value={paidForDate}
              onChange={setPaidForDate}
            />
          </FormField>
          <FormField label={t("Método")}>
            <select
              data-testid="payment-method"
              className="cm-input"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="zelle">Zelle</option>
              <option value="venmo">Venmo</option>
              <option value="cash">{t("Dinheiro")}</option>
              <option value="check">{t("Cheque")}</option>
              <option value="other">{t("Outro")}</option>
            </select>
          </FormField>
          <FormField label={t("Observações")}>
            <textarea
              data-testid="payment-notes"
              rows={2}
              className="cm-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={saving}>{t("Cancelar")}</Button>
          <Button onClick={save} disabled={saving} data-testid="payment-save">
            {saving ? t("Salvando…") : t("Registrar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PaymentLinkButton({ receivable }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [amount, setAmount] = useState(receivable.value || 0);

  async function generate() {
    setLoading(true);
    try {
      const { data } = await api.post("/payment-links/receivable", {
        client_id: receivable.client_id,
        amount: Number(amount),
        origin: window.location.origin,
      });
      setLink(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha ao gerar link"));
    } finally {
      setLoading(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success(t("Link copiado"));
    } catch {
      toast.error(t("Não foi possível copiar"));
    }
  }

  return (
    <>
      <Button
        data-testid={`receivable-link-${receivable.client_id}`}
        onClick={() => { setOpen(true); setLink(null); setAmount(receivable.value || 0); }}
        size="sm"
        variant="outline"
        className="border-blue-300 text-blue-700 hover:bg-blue-50"
      >
        <CreditCard size={13} className="mr-1" /> {t("Cobrar")}
      </Button>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="payment-link-dialog">
          <DialogHeader>
            <DialogTitle>{t("Gerar link de pagamento")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {t("Cobrar")} <strong>{receivable.client_name}</strong> {t("via Stripe Checkout (cartão de crédito).")}
          </p>

          {!link ? (
            <div className="space-y-3 mt-2">
              <FormField label={t("Valor a cobrar (USD)")} required>
                <DollarInput value={amount} onChange={setAmount} testId="payment-link-amount" />
              </FormField>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
                  {t("Cancelar")}
                </Button>
                <Button
                  data-testid="payment-link-generate"
                  onClick={generate}
                  disabled={loading || !amount || amount <= 0}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <CreditCard size={14} className="mr-2" />}
                  {t("Gerar link")}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{t("Link gerado")}</div>
                <div className="text-xs font-mono text-slate-700 break-all mt-1">{link.url}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={link.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="payment-link-whatsapp"
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                >
                  <Send size={14} /> WhatsApp
                </a>
                <a
                  href={link.mailto_url}
                  data-testid="payment-link-email"
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                >
                  <Mail size={14} /> Email
                </a>
                <button
                  type="button"
                  onClick={copy}
                  data-testid="payment-link-copy"
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold"
                >
                  <Copy size={14} /> {t("Copiar")}
                </button>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold"
                >
                  <ExternalLink size={14} /> {t("Abrir")}
                </a>
              </div>
              <p className="text-[11px] text-slate-500">
                {t("O pagamento é registrado automaticamente quando o cliente concluir.")}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
